import React from 'react'
import {InView} from 'react-intersection-observer'

import {pageActioned, PageActionedAction} from '@/analytics/segment'
import {Chat} from '@/sections/chat'
import {Comparison} from '@/sections/comparison'
import {ComparisonTable} from '@/sections/comparison-table'
import {ContactEmails} from '@/sections/contact-emails'
import {ContactForm} from '@/sections/contact-form'
import {ContactHero} from '@/sections/contact-hero'
import {Countdown} from '@/sections/countdown'
import {Highlight, isAllowedBackgroundColor} from '@/sections/highlight'
import HubspotHeroForm from '@/sections/hubspot-hero-form/hubspot-hero-form'
import {IntegrationOverview} from '@/sections/integration-overview'
import {IntegrationCards} from '@/sections/integrations-cards'
import {Steps} from '@/sections/steps'
import {TitleWithSideText} from '@/sections/title-with-side-text'
import {TwoImageHero} from '@/sections/two-image-hero'
import {FeatureBlockWithMarquee} from '@/templates/homepage-v4/feature-block-with-marquee'
import {StickyCards} from '@/templates/homepage-v4/sticky-cards'
import exhaustiveCheck from '@/utils/exhaustive-check'
import {getVideoSources} from '@/utils/get-video-sources'
import {isEmptyObject} from '@/utils/is-empty-object'

import type {SlicePageQuery, CustomerCaseQuery, PartnerTemplateQuery} from '@/graphql-types'

import {CardGridSlice} from './card-grid'
import {ComponentPlaceholderSlice} from './component-placeholder'
import {ContentPlusBlocksSlice} from './content-plus-blocks'
import {ContentPlusShortBulletsSlice} from './content-plus-short-bullets'
import {CustomerLogosWithCta} from './customer-logos-cta'
import {CustomersSlice} from './customers'
import {CustomersLogosPlusHeadlineSlice} from './customers-logos-plus-headline'
import {EventCardsSlice} from './event-cards'
import {FAQSlice} from './faq'
import {FeatureBlockSlice} from './feature-block'
import {FeatureGridSlice} from './feature-grid'
import {GetStartedSlice} from './get-started'
import {GlassdoorWidgetSlice} from './glassdoor-widget'
import {HeroSlice} from './hero'
import {HubspotFormSlice} from './hubspot-form'
import {IntegrationsSlice} from './integrations'
import {IntegrationsGridSlice} from './integrations-grid'
import {InteractiveDemoSlice} from './interactive-demo'
import {JobsGridSlice} from './jobs-grid'
import {LandingHeroSlice} from './landing-hero'
import {MagnetHeroSlice} from './magent-hero'
import {MediaItemGridSlice} from './media-item-grid'
import {OnDemandResourcesSlice} from './on-demand-resources'
import {PanelGridSlice} from './panel-grid'
import {PartnerProgrammeSlice} from './partner-programme'
import {PricingSlice} from './pricing'
import {RatingsPlusHeadlineSlice} from './ratings-plus-headline'
import {ReviewsGridSlice} from './reviews-grid'
import {RichTextSlice} from './rich-text'
import {SimpleCardGridSlice} from './simple-card-grid'
import {SliderCardsSlice} from './slider-cards'
import {SocialProofSlice} from './social-proof'
import {StatisticsSlice} from './statistics'
import {TableSlice} from './table'
import {UpcomingEventsSlice} from './upcoming-events'
import {VideoSlice} from './video'
import {VideoLibrarySlice} from './video-library'
import {VideoPopoverWithTextSlice} from './video-popover-with-text'

type SliceData =
    | SlicePageQuery['prismicSlicePage']['data']['body'][number]
    | CustomerCaseQuery['allPrismicTemplateCustomerCase']['nodes'][number]['data']['body'][number]
    | PartnerTemplateQuery['allPrismicTemplatePartner']['nodes'][number]['data']['body'][number]

type Props = {
    slices: SliceData[]
    trackVisibleSections?: boolean
}

export type SliceProps = {
    slice: SliceData
    sectionKey: string
}

const Slice = ({sectionKey, slice}: SliceProps) => {
    const typename = slice.__typename

    switch (typename) {
        case 'PrismicSlicePageDataBodyHero':
            return <HeroSlice key={sectionKey} slice={slice} dataTestId={sectionKey} />

        case 'PrismicSlicePageDataBodyCustomers':
            return <CustomersSlice key={sectionKey} slice={slice} dataTestId={sectionKey} />

        case 'PrismicSlicePageDataBodyFeatureBlock':
        case 'PrismicTemplatePartnerDataBodyFeatureBlock':
            return <FeatureBlockSlice key={sectionKey} slice={slice} dataTestId={sectionKey} />

        case 'PrismicSlicePageDataBodyFaq':
            return <FAQSlice key={sectionKey} slice={slice} dataTestId={sectionKey} />

        case 'PrismicSlicePageDataBodySocialProof':
        case 'PrismicTemplatePartnerDataBodySocialProof':
            return <SocialProofSlice key={sectionKey} slice={slice} dataTestId={sectionKey} />

        case 'PrismicSlicePageDataBodyGetStarted':
        case 'PrismicTemplatePartnerDataBodyGetStarted':
        case 'PrismicTemplateCustomerCaseDataBodyGetStarted':
            return <GetStartedSlice key={sectionKey} slice={slice} dataTestId={sectionKey} />

        case 'PrismicSlicePageDataBodyMediaItemGrid':
        case 'PrismicTemplateCustomerCaseDataBodyMediaItemGrid':
        case 'PrismicTemplatePartnerDataBodyMediaItemGrid':
            return <MediaItemGridSlice key={sectionKey} slice={slice} dataTestId={sectionKey} />

        case 'PrismicSlicePageDataBodyFeatureGrid':
            return <FeatureGridSlice key={sectionKey} slice={slice} dataTestId={sectionKey} />

        case 'PrismicSlicePageDataBodyContentPlusBlocks':
            return <ContentPlusBlocksSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyCustomersLogosPlusHeadline':
        case 'PrismicTemplatePartnerDataBodyCustomersLogosPlusHeadline':
            return (
                <CustomersLogosPlusHeadlineSlice
                    key={sectionKey}
                    slice={slice}
                    dataTestId={sectionKey}
                />
            )

        case 'PrismicSlicePageDataBodyLandingHero':
            return <LandingHeroSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyRatingsPlusHeadline':
        case 'PrismicTemplatePartnerDataBodyRatingsPlusHeadline':
            return (
                <RatingsPlusHeadlineSlice key={sectionKey} slice={slice} dataTestId={sectionKey} />
            )

        case 'PrismicSlicePageDataBodyVideo':
            return <VideoSlice key={sectionKey} slice={slice} dataTestId={sectionKey} />

        case 'PrismicSlicePageDataBodyPricing':
            return <PricingSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyStats':
            return <StatisticsSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyCardGrid':
        case 'PrismicTemplateCustomerCaseDataBodyCardGrid':
            return <CardGridSlice key={sectionKey} slice={slice} dataTestId={sectionKey} />

        case 'PrismicSlicePageDataBodyHubspotForm':
            return <HubspotFormSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyComponentPlaceholder':
            return <ComponentPlaceholderSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyContentPlusShortBullets':
            return <ContentPlusShortBulletsSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodySliderCards':
            return <SliderCardsSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyTwoImageHero':
            return (
                <TwoImageHero
                    key={sectionKey}
                    title={slice.primary.title}
                    content={slice.primary.content}
                    videoId={slice.primary.video_id}
                    imageLeft={slice.primary.image_left}
                    imageRight={slice.primary.image_right}
                    imageButton={slice.primary.image_button}
                    ctaText={slice.primary.cta_text}
                    ctaLink={slice.primary.cta_link}
                    dataTestId={sectionKey}
                />
            )

        case 'PrismicSlicePageDataBodyCountdown':
            return (
                <Countdown
                    key={sectionKey}
                    heading={slice.primary.headingText}
                    descriptionText={slice.primary.description_text}
                    dateTime={slice.primary.target_date_time}
                    ctaText={slice.primary.cta_text}
                    ctaLink={slice.primary.cta_link}
                />
            )

        case 'PrismicSlicePageDataBodyHighlight':
            const highlightColor = isAllowedBackgroundColor(slice.primary.highlight_color)
                ? slice.primary.highlight_color
                : 'muted'

            return (
                <Highlight
                    key={sectionKey}
                    title={slice.primary.title}
                    mirrored={slice.primary.mirrored}
                    highlightColor={highlightColor}
                    items={slice.items.map((item) => {
                        const videoSources = getVideoSources({
                            mp4: item.video_mp4,
                            webm: item.video_webm,
                        })
                        return {
                            ...item,
                            title: item.slide_title,
                            body: item.slide_body.richText,
                            ctaText: item.cta_text,
                            ctaLink: item.cta_link,
                            video: videoSources,
                            trackingEvent: item.tracking_event,
                        }
                    })}
                />
            )

        case 'PrismicSlicePageDataBodyComparisonTable':
            return (
                <ComparisonTable
                    key={sectionKey}
                    heading={slice.primary.section_heading}
                    subheading={slice.primary.section_subheading}
                    tableData={{
                        column1Heading: slice.primary.column_1_heading,
                        column2Heading: slice.primary.column_2_heading,
                        rows: slice.items.map((row) => ({
                            heading: row.heading,
                            data1: row.data_1,
                            data2: row.data_2,
                        })),
                    }}
                />
            )

        case 'PrismicSlicePageDataBodySteps':
            return (
                <Steps
                    key={sectionKey}
                    title={slice.primary.title}
                    subtitle={slice.primary.description.richText}
                    showCount={slice.primary.show_count}
                    items={slice.items}
                    dataTestId={sectionKey}
                    hasSmallCopy={slice.primary.has_small_copy}
                />
            )

        case 'PrismicSlicePageDataBodyGlassdoorWidget':
            return <GlassdoorWidgetSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyVideoPopoverWithText':
            return (
                <VideoPopoverWithTextSlice key={sectionKey} slice={slice} dataTestId={sectionKey} />
            )

        case 'PrismicSlicePageDataBodyJobsGrid':
            return <JobsGridSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyTitleWithSideText':
            return (
                <TitleWithSideText
                    key={sectionKey}
                    title={slice.primary.title}
                    ctaLink={slice.primary.cta_link}
                    ctaLabel={slice.primary.cta_label}
                    copy={slice.primary.copy.richText}
                />
            )

        case 'PrismicSlicePageDataBodyIntegrationCards':
            return (
                <IntegrationCards
                    key={sectionKey}
                    title={slice.primary.title}
                    subtitle={slice.primary.subtitle}
                    logos={slice.items.map(({name, cta_link, image}) => ({
                        title: name,
                        link: cta_link,
                        linkText: slice.primary.cta_text,
                        logo: image,
                    }))}
                />
            )

        case 'PrismicSlicePageDataBodyContactHero':
            return (
                <ContactHero
                    key={sectionKey}
                    headline={slice.primary.headline}
                    items={slice.items.map(({title, description, image, cta_link, cta_text}) => ({
                        ctaLink: cta_link,
                        ctaText: cta_text,
                        title,
                        description,
                        image,
                    }))}
                />
            )

        case 'PrismicSlicePageDataBodyContactEmails':
            return (
                <ContactEmails
                    key={sectionKey}
                    title={slice.primary.title}
                    body={slice.primary.body1}
                    items={slice.items.map(({contact_title, contact_email, destination_link}) => ({
                        title: contact_title,
                        destination: contact_email,
                        destinationLink: destination_link,
                    }))}
                />
            )

        case 'PrismicSlicePageDataBodyContactForm':
            return (
                <ContactForm
                    key={sectionKey}
                    title={slice.primary.title}
                    image={slice.primary.image}
                    successTitle={slice.primary.success_title}
                    successBody={slice.primary.success_body_support.richText}
                    successCta={slice.primary.success_cta}
                    successImage={slice.primary.success_image}
                    privacyDisclaimer={slice.primary.privacy_disclaimer.richText}
                />
            )

        case 'PrismicSlicePageDataBodyChat':
            return (
                <Chat
                    key={sectionKey}
                    title={slice.primary.title}
                    body={slice.primary.body1}
                    ctaText={slice.primary.cta_text}
                    image={slice.primary.image}
                />
            )

        case 'PrismicSlicePageDataBodyIntegrations':
            return <IntegrationsSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodySimpleCardGrid':
            return <SimpleCardGridSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyRichText':
            return <RichTextSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyCustomerLogosPlusCta':
            return (
                <CustomerLogosWithCta
                    key={sectionKey}
                    ctaText={slice.primary.cta_text}
                    ctaLink={slice.primary.cta_link}
                    title={slice.primary.title}
                    logos={slice.items.map(({logo}) => logo)}
                    isMarquee={slice.primary.marquee_mode}
                    backgroundColor={slice.primary.background_color}
                />
            )

        case 'PrismicSlicePageDataBodyEventCards':
            return (
                <EventCardsSlice
                    key={sectionKey}
                    title={slice.primary.title}
                    eventCtaText={slice.primary.event_cta_text}
                    items={slice.items}
                />
            )

        case 'PrismicSlicePageDataBodyVideoLibrary':
            return <VideoLibrarySlice key={sectionKey} title={slice.primary.title} />

        case 'PrismicSlicePageDataBodyPartnerProgramme':
            return (
                <PartnerProgrammeSlice
                    key={sectionKey}
                    title={slice.primary.title}
                    tables_tick_screen_reader_text={slice.primary.tables_tick_screen_reader_text}
                    items={slice.items}
                />
            )

        case 'PrismicSlicePageDataBodyInteractiveDemo':
            return <InteractiveDemoSlice key={sectionKey} {...slice} />

        case 'PrismicSlicePageDataBodyUpcomingEvents':
            return <UpcomingEventsSlice key={sectionKey} {...slice} />

        case 'PrismicSlicePageDataBodyOnDemandResources':
            return <OnDemandResourcesSlice key={sectionKey} {...slice} />

        case 'PrismicSlicePageDataBodyMagnetHero':
            return <MagnetHeroSlice key={sectionKey} {...slice.primary} />

        case 'PrismicSlicePageDataBodyTable':
            return <TableSlice key={sectionKey} {...slice} />

        case 'PrismicSlicePageDataBodyPanelGrid':
            return <PanelGridSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyIntegrationsGrid':
            return <IntegrationsGridSlice key={sectionKey} {...slice} />

        case 'PrismicSlicePageDataBodyIntegrationOverview':
            return (
                <IntegrationOverview
                    key={sectionKey}
                    title={slice.primary.title}
                    developers_label={slice.primary.developers_label}
                    developers={slice.primary.developers}
                    links_label={slice.primary.links_label}
                    links={slice.primary.links.richText}
                    copy={slice.primary.copy.richText}
                />
            )

        case 'PrismicSlicePageDataBodyStickyCards':
            return <StickyCards key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyComparison':
            return <Comparison key={sectionKey} content={slice} />

        case 'PrismicSlicePageDataBodyReviewsGrid':
            return <ReviewsGridSlice key={sectionKey} slice={slice} />

        case 'PrismicSlicePageDataBodyHubspotHeroForm':
            return <HubspotHeroForm key={sectionKey} content={slice} />

        case 'PrismicSlicePageDataBodyFeatureBlockWithMarquee':
            return <FeatureBlockWithMarquee key={sectionKey} slice={slice} />

        default:
            exhaustiveCheck(typename)

            throw new Error(`Unknown slice: ${JSON.stringify(slice)}`)
    }
}

const getSliceSectionName = (type: SliceData['__typename']) => {
    const sliceSectionName = type
        .split('PrismicSlicePageDataBody')[1]
        .split(/(?=[A-Z])/)
        .join(' ')

    if (!sliceSectionName) {
        throw new Error('Unknown slice section name')
    }

    return sliceSectionName
}

export const SliceZone = ({slices, trackVisibleSections = false}: Props) => {
    return (
        <>
            {slices.map((slice, i) => {
                // New slices added in Prismic will show up as empty objects
                // until we explicitly query content for them
                if (isEmptyObject(slice)) {
                    return null
                }

                if (!slice.__typename) {
                    throw new Error(`Missing __typename in slice: ${JSON.stringify(slice)}`)
                }

                const type = slice.__typename
                const key = `${type}-${i}`

                return (
                    <InView
                        key={key}
                        triggerOnce
                        skip={!trackVisibleSections}
                        threshold={1}
                        onChange={(inView) => {
                            if (inView) {
                                pageActioned({
                                    action: PageActionedAction.SectionViewed,
                                    section: `Section ${i + 1}: ${getSliceSectionName(type)}`,
                                })
                            }
                        }}
                    >
                        <Slice sectionKey={key} slice={slice} />
                    </InView>
                )
            })}
        </>
    )
}
